import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Check, X, Trash2, Heart, RefreshCw, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_TABS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

const StatusBadge = ({ status }) => {
  const map = {
    approved: 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40',
    pending: 'bg-yellow-900/40 text-yellow-300 border-yellow-700/40',
    rejected: 'bg-red-900/40 text-red-300 border-red-700/40',
  };
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest border ${
        map[status] || map.approved
      }`}
    >
      {status}
    </span>
  );
};

const CommentsModerationTab = ({ token }) => {
  const [data, setData] = useState({ totals: {}, comments: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const q = filter === 'all' ? '' : `?status=${filter}`;
      const res = await fetch(`${API}/api/admin/comments${q}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load comments');
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [filter, token]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const moderate = async (id, status) => {
    setBusyId(id);
    try {
      const res = await fetch(`${API}/api/admin/comments/${id}/moderate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Moderation failed');
      await fetchComments();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const hardDelete = async (id) => {
    if (!window.confirm('Permanently delete this comment? This cannot be undone.')) return;
    setBusyId(id);
    try {
      const res = await fetch(`${API}/api/admin/comments/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      await fetchComments();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const totals = data.totals || {};

  const formatTime = useMemo(() => (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  }, []);

  return (
    <div className="space-y-5" data-testid="comments-moderation-tab">
      {/* Filter pills */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {STATUS_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              data-testid={`comments-filter-${t.id}`}
              className={`px-3 py-1.5 rounded text-xs font-bold transition-colors flex items-center gap-1.5 ${
                filter === t.id
                  ? 'bg-red-600 text-white'
                  : 'bg-zinc-900 text-gray-400 hover:text-white border border-red-900/30'
              }`}
            >
              {t.label}
              <span className="bg-black/40 px-1.5 py-0.5 rounded text-[10px] tabular-nums">
                {totals[t.id] ?? 0}
              </span>
            </button>
          ))}
        </div>
        <button
          onClick={fetchComments}
          data-testid="comments-refresh-btn"
          className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-red-900/30 text-gray-300 hover:text-white font-bold px-3 py-1.5 rounded text-xs transition-colors"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded text-sm" data-testid="comments-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-400" data-testid="comments-loading">Loading comments...</div>
      ) : data.comments.length === 0 ? (
        <div className="bg-zinc-900/60 border border-red-900/30 rounded-lg p-8 text-center text-gray-400" data-testid="comments-empty">
          No {filter === 'all' ? '' : filter} comments.
        </div>
      ) : (
        <div className="space-y-3" data-testid="comments-list">
          {data.comments.map((c) => (
            <div
              key={c.id}
              data-testid={`mod-comment-${c.id}`}
              className="bg-zinc-900/80 backdrop-blur-xl border border-red-900/30 rounded-lg p-4"
            >
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-red-400 font-black text-sm">{c.author_name}</span>
                  <span className="text-gray-500 text-xs">on</span>
                  <span className="text-white font-bold text-sm">{c.video_title}</span>
                  <StatusBadge status={c.status} />
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500 shrink-0">
                  <span className="flex items-center gap-1">
                    <Heart className="h-3 w-3" /> {c.likes || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {formatTime(c.created_at)}
                  </span>
                </div>
              </div>

              <p className="text-gray-200 text-sm whitespace-pre-wrap break-words mb-3">
                {c.comment_text}
              </p>

              <div className="flex items-center gap-2 flex-wrap">
                {c.status !== 'approved' && (
                  <button
                    onClick={() => moderate(c.id, 'approved')}
                    disabled={busyId === c.id}
                    data-testid={`mod-approve-${c.id}`}
                    className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs px-3 py-1.5 rounded disabled:opacity-50"
                  >
                    <Check className="h-3.5 w-3.5" /> Approve
                  </button>
                )}
                {c.status !== 'rejected' && (
                  <button
                    onClick={() => moderate(c.id, 'rejected')}
                    disabled={busyId === c.id}
                    data-testid={`mod-reject-${c.id}`}
                    className="flex items-center gap-1 bg-yellow-700 hover:bg-yellow-600 text-white font-bold text-xs px-3 py-1.5 rounded disabled:opacity-50"
                  >
                    <X className="h-3.5 w-3.5" /> Reject
                  </button>
                )}
                {c.status !== 'pending' && (
                  <button
                    onClick={() => moderate(c.id, 'pending')}
                    disabled={busyId === c.id}
                    data-testid={`mod-pending-${c.id}`}
                    className="flex items-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-gray-300 font-bold text-xs px-3 py-1.5 rounded disabled:opacity-50"
                  >
                    Move to Pending
                  </button>
                )}
                <button
                  onClick={() => hardDelete(c.id)}
                  disabled={busyId === c.id}
                  data-testid={`mod-delete-${c.id}`}
                  className="flex items-center gap-1 bg-red-700/40 hover:bg-red-600/60 text-red-200 font-bold text-xs px-3 py-1.5 rounded ml-auto disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentsModerationTab;
